<template>
    <div>
        <h1 class="text-h5 my-5">
            Team members
            <v-progress-circular v-if="loading" indeterminate color="green" class="ml-2"/>
        </h1>
        <v-alert v-if="alert.msg" :color="alert.color" icon="mdi-alert-octagon-outline" outlined text dismissible>
            {{alert.msg}}
        </v-alert>

        <v-simple-table v-if="access === 'edit' || access === 'view'" dense class="mb-10">
            <thead>
            <tr>
                <th>Email</th>
                <th>Role</th>
                <th>Status</th>
                <th></th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="u in users" :key="u.email">
                <td>{{u.email}}</td>
                <td>{{u.role}}</td>
                <td>{{u.status}}</td>
                <td>
                    <v-btn v-if="access === 'edit' && u.role !== 'owner'" icon title="revoke access" @click="remove(u.email)">
                        <v-icon small>mdi-account-remove</v-icon>
                    </v-btn>
                </td>
            </tr>
            </tbody>
        </v-simple-table>

        <template v-if="access === 'edit'">
            <template v-if="!saml.active">
                <h2 class="text-h6 my-5">Invite a teammate</h2>
                <v-alert v-if="form.alert.msg" :color="form.alert.color" icon="mdi-alert-octagon-outline" outlined text dismissible>
                    {{form.alert.msg}}
                </v-alert>
                <v-form v-model="form.valid" @submit.prevent="invite" ref="form" class="mt-5">
                    <div class="subtitle-1">Email</div>
                    <v-text-field outlined dense type="email" v-model="form.email" :rules="[$validators.isEmail]" />
                    <div class="subtitle-1">Role</div>
                    <v-radio-group v-model="form.role" class="mt-0">
                        <v-radio v-for="(description, name) in roles" :key="name" :value="name">
                            <template #label>
                            <span class="mt-0">
                                <span>{{name}}</span> <span class="caption grey--text">{{description}}</span>
                            </span>
                            </template>
                        </v-radio>
                    </v-radio-group>
                </v-form>
                <v-btn color="primary" @click="invite" :disabled="!form.valid" :loading="form.loading">
                    Invite
                </v-btn>
            </template>

            <h2 class="text-h5 mb-3 mt-10">Single Sign-On (SAML)</h2>
            <v-alert v-if="saml.alert.msg" :color="saml.alert.color" icon="mdi-alert-octagon-outline" outlined text dismissible>
                {{saml.alert.msg}}
            </v-alert>
            <div v-if="!saml.available" class="mb-3 grey--text">
                A part of the Enterprise plan. Please review your <router-link :to="{params: {tab: 'billing'}}">subscription details</router-link>.
            </div>
            <v-simple-table v-if="saml.available || saml.active" dense>
                <tbody>
                <tr>
                    <td class="font-weight-medium text-no-wrap">Identity Provider:</td>
                    <td>
                        <v-icon v-if="saml.active" color="success" class="mr-1" size="20">mdi-check-circle</v-icon>
                        <span style="vertical-align: middle">{{saml.provider}}</span>
                        <input ref="file" type="file" accept=".xml" @change="samlUpload" class="d-none">
                        <v-btn v-if="!saml.active" color="primary" @click="$refs.file.click()" :disabled="saml.loading" small class="my-1">
                            <template>Upload Identity Provider Metadata XML</template>
                        </v-btn>
                        <v-btn v-else @click="saml.delete = true" :loading="saml.loading" :disabled="saml.loading" small icon>
                            <v-icon small>mdi-trash-can-outline</v-icon>
                        </v-btn>
                    </td>
                </tr>
                <tr v-if="saml.active">
                    <td class="font-weight-medium text-no-wrap">Single Sign-On URL:</td>
                    <td>
                        <a :href="saml.sso_url" target="_blank">{{saml.sso_url}} <v-icon small>mdi-open-in-new</v-icon></a>
                    </td>
                </tr>
                <tr>
                    <td class="font-weight-medium text-no-wrap">Service Provider Issuer / Identity ID:</td>
                    <td>
                        {{saml.sso_url}}
                        <v-btn @click="copy(saml.sso_url)" small icon><v-icon small>mdi-content-copy</v-icon></v-btn>
                    </td>
                </tr>
                <tr>
                    <td class="font-weight-medium text-no-wrap">Service Provider ACS URL:</td>
                    <td>
                        {{saml.sso_url}}
                        <v-btn @click="copy(saml.sso_url)" small icon><v-icon small>mdi-content-copy</v-icon></v-btn>
                    </td>
                </tr>
                <tr>
                    <td class="font-weight-medium text-no-wrap">Attribute mapping:</td>
                    <td>
                        Coroot expects to receive the <span class="font-weight-medium">Email</span>, <span class="font-weight-medium">FirstName</span>, and <span class="font-weight-medium">LastName</span> attributes.
                        <br>
                        Please configure Attribute Mapping on your Identity Provider's side.
                    </td>
                </tr>
                </tbody>
            </v-simple-table>
            <v-dialog v-model="saml.delete" max-width="800">
                <v-card class="pa-5">
                    <div class="d-flex align-center font-weight-bold mb-2">
                        Disable Single Sign-On
                        <v-spacer />
                        <v-btn icon @click="saml.delete = false"><v-icon>mdi-close</v-icon></v-btn>
                    </div>
                    <p>This action will delete Identity Provider metadata and disable Single Sign-On.</p>

                    <v-btn color="error" @click="samlDelete()">
                        Delete
                    </v-btn>
                </v-card>
            </v-dialog>
        </template>
    </div>
</template>

<script>
export default {
    props: {
        workspaceId: String,
    },

    data() {
        return {
            users: [],
            access: '',
            loading: false,
            alert: {msg: '', color: ''},

            form: {
                loading: false,
                alert: {msg: '', color: ''},
                email: '',
                role: 'user',
                valid: false,
            },

            saml: {
                configure: false,
                loading: false,
                delete: false,
                alert: {msg: '', color: ''},
                available: false,
                active: false,
                provider: '',
                sso_url: '',
            },
        };
    },

    mounted() {
        this.get();
    },

    computed: {
        roles() {
            return {
                admin: 'owner-like',
                user: 'regular user',
                guest: 'readonly user',
            };
        },
    },

    methods: {
        get() {
            this.loading = true;
            this.$api.users(this.workspaceId, null, (data, meta) => {
                this.loading = false;
                if (meta.error) {
                    this.alert.msg = 'Failed to get users. Please try again later.'
                    this.alert.color = 'red';
                    return;
                }
                this.access = data.access;
                if (!this.access) {
                    this.alert.msg = 'You are not permitted to view the team details.';
                    this.alert.color = 'primary';
                } else if (this.access !== 'edit') {
                    this.alert.msg = 'Only the workspace owner and admins are allowed to manage the team.';
                    this.alert.color = 'primary';
                }
                this.users = data.users;
                this.saml.available = data.saml && data.saml.available;
                this.saml.active = data.saml && data.saml.active;
                this.saml.provider = data.saml && data.saml.provider;
                this.saml.sso_url = data.saml && data.saml.sso_url;
            });
        },
        invite() {
            this.form.loading = true;
            this.form.alert = {msg: '', color: ''};
            this.$api.users(this.workspaceId, {action: 'invite', email: this.form.email, role: this.form.role}, (data, meta) => {
                this.form.loading = false;
                if (meta.error) {
                    this.form.alert.msg = 'Failed to send invitation: ' + meta.error;
                    this.form.alert.color = 'error';
                } else {
                    this.form.alert.msg = 'Invitation successfully sent to '+ this.form.email;
                    this.form.alert.color = 'success';
                    this.form.email = '';
                    this.form.role = 'user';
                    this.$refs.form.resetValidation();
                }
                this.get();
            });
        },
        remove(email) {
            this.loading = true;
            this.alert = {msg: '', color: ''};
            this.$api.users(this.workspaceId, {action: 'revoke', email: email}, (data, meta) => {
                this.form.loading = false;
                if (meta.error) {
                    this.alert.msg = 'Failed to revoke access: ' + meta.error;
                    this.alert.color = 'error';
                } else {
                    this.alert.msg = email + ' has been removed from the workspace';
                    this.alert.color = 'success';
                }
                this.get();
            });
        },
        samlPost(action, metadata) {
            this.saml.loading = true;
            this.saml.alert = {msg: '', color: ''};
            this.$api.sso(this.workspaceId, {action, saml: {metadata}}, (data, meta) => {
                this.saml.loading = false;
                this.saml.configure = false;
                if (meta.error) {
                    this.saml.alert.msg = meta.error;
                    this.saml.alert.color = 'error';
                }
                this.get();
            });

        },
        samlUpload(e) {
            const file = e.target.files[0];
            e.target.value = '';
            if (!file) {
                return;
            }
            file.text().then(v => {
                this.samlPost('update', v);
            })
        },
        samlDelete() {
            this.saml.delete = false;
            this.samlPost('delete');
        },
        copy(text) {
            navigator.clipboard.writeText(text);
        }
    },
};
</script>

<style scoped>

</style>
